// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

import { assertArray, assertDictionary, assertInstanceOf, assertString } from '../../utils/assertType.js';
import { createTranscriptionMediaFileMeta } from './TranscriptionMediaFileStore.js';

/**
 * Define the possible status of a transcription job in the frontend app
 */
export const jobstatus_pending = 'pending';
export const jobstatus_transcribing = 'transcribing';
export const jobstatus_translating = 'translating';
export const jobstatus_done = 'done';
export const jobstatus_transcription_downloaded = 'transcription_downloaded';
/** A list of allowed status for a transcription job */
const allowed_jobstatus = [
  jobstatus_pending,
  jobstatus_transcribing,
  jobstatus_translating,
  jobstatus_done,
  jobstatus_transcription_downloaded,
];

/**
 * Define the possible errors for a transcription job in the frontend app
 */
export const joberror_http = 'http';
export const joberror_missing = 'missing_job';
export const joberror_backend = 'backend';
export const joberror_deleted = 'deleted';
export const joberror_any = 'any';
/** A list of allowed errors for a transcription job */
const allowed_errors = [joberror_http, joberror_missing, joberror_backend, joberror_deleted, joberror_any];

/**
 * A class representing a transcription job in the frontend app
 *
 * @param {string} job_id - the job id received from the backend
 * @param {string} file_name - the file name of the corresponding file that the user uploaded for transcription
 * @param {string} status  - status of the transcription job in the frontend app. @see {@link allowed_jobstatus}
 * @param {?string} error - if set an error. @see {@link allowed_errors}
 * @param {string} error_message - a text shown to the user in case an error appeared. Must not be empty if an error is set.
 * @param {?SingleTranscript} transcription - the transcription object related to the transcription job
 */
export class TranscriptionJob {
  constructor(
    job_id,
    file_name,
    status = jobstatus_pending,
    error = null,
    error_message = '',
    transcription = null,
    media_file_meta = null,
  ) {
    assertString(job_id, 'job_id');
    assertString(file_name, 'file_name');
    if (transcription !== null) {
      assertInstanceOf(transcription, SingleTranscript, 'transcription');
    }
    this.job_id = job_id;
    this.file_name = file_name;
    this.setStatus(status);
    this.setError(error, error_message);
    this.transcription = transcription;
    this.media_file_meta = normalizeMediaFileMeta(media_file_meta);
  }

  getJobId() {
    return this.job_id;
  }

  getJobStatus() {
    return this.status;
  }

  getFileName() {
    return this.file_name;
  }

  setError(new_error = null, new_error_message = '') {
    if (new_error !== null) {
      assertString(new_error, 'error');
      if (!allowed_errors.includes(new_error)) {
        throw new TypeError(`${new_error} is not an allowed error.`);
      }
      assertString(new_error_message, 'error_message');
      if (new_error_message === '') {
        throw new TypeError('When an error is set an error message must be set as well.');
      }
    }
    this.error = new_error;
    this.error_message = new_error_message;
  }

  getError() {
    return this.error;
  }

  getErrorMessage() {
    return this.error_message;
  }

  hasError() {
    return this.error !== null;
  }

  setTranscription(job_transcription) {
    this.transcription = job_transcription;
  }

  setStatus(new_status) {
    assertString(new_status, 'status');
    if (!allowed_jobstatus.includes(new_status)) {
      throw new TypeError(`${new_status} is not an allowed status.`);
    }
    this.status = new_status;
  }

  /** Checks if the transcription / translation is in process in the backend */
  isInProcess() {
    const process_status = [jobstatus_pending, jobstatus_transcribing, jobstatus_translating];
    return process_status.includes(this.getJobStatus());
  }

  getTranscription() {
    return this.transcription;
  }

  setMediaFileMeta(fileOrMeta) {
    this.media_file_meta =
      typeof File !== 'undefined' && fileOrMeta instanceof File
        ? createTranscriptionMediaFileMeta(fileOrMeta)
        : normalizeMediaFileMeta(fileOrMeta);
  }

  getMediaFileMeta() {
    return this.media_file_meta;
  }

  toJSON() {
    return {
      job_id: this.job_id,
      file_name: this.file_name,
      status: this.status,
      error: this.error,
      error_message: this.error_message,
      transcription: this.transcription ? this.transcription.toJSON() : null,
      media_file_meta: this.media_file_meta,
    };
  }

  static fromJSON(json) {
    if (!json) return null;

    return new TranscriptionJob(
      json.job_id,
      json.file_name,
      json.status,
      json.error,
      json.error_message,
      json.transcription ? SingleTranscript.fromJSON(json.transcription) : null,
      json.media_file_meta ?? null,
    );
  }
}

function normalizeMediaFileMeta(meta) {
  if (!isRecord(meta)) return null;

  return {
    name: typeof meta.name === 'string' ? meta.name : '',
    size: typeof meta.size === 'number' ? meta.size : null,
    type: typeof meta.type === 'string' ? meta.type : '',
    lastModified: typeof meta.lastModified === 'number' ? meta.lastModified : null,
  };
}

function isRecord(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getResultSegments(result) {
  return result.segments.filter((segment) => isRecord(segment));
}

function getSegmentSubsegments(segment) {
  return segment.subsegments.filter((subsegment) => isRecord(subsegment));
}

function subsegmentText(subsegment) {
  return typeof subsegment.text === 'string' ? subsegment.text.trim() : '';
}

function segmentText(segment) {
  return getSegmentSubsegments(segment)
    .map((subsegment) => subsegmentText(subsegment))
    .filter((text) => text !== '')
    .join(' ');
}

function normalizeResult(result) {
  const segments = getResultSegments(result).map((segment) => ({
    ...segment,
    text: segmentText(segment),
  }));

  return {
    ...result,
    segments,
    text: segments
      .map((segment) => segment.text)
      .filter((text) => text !== '')
      .join(' '),
  };
}

function formatTimestamp(start, end) {
  if (typeof start !== 'number' || typeof end !== 'number' || !Number.isFinite(start) || !Number.isFinite(end)) {
    return '';
  }

  const toHHMMSS = (value) => {
    const totalSeconds = Math.max(0, Math.round(value));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  return `${toHHMMSS(start)} - ${toHHMMSS(end)}`;
}

function segmentSpeaker(segment) {
  return typeof segment.speaker === 'string' ? segment.speaker : '';
}

function logprobToPercent(logprob) {
  if (typeof logprob !== 'number' || !Number.isFinite(logprob)) {
    return 100;
  }

  const probability = Math.exp(logprob);
  return Math.max(0, Math.min(100, probability * 100));
}

function buildPartsFromSegment(segment) {
  return getSegmentSubsegments(segment)
    .map((subsegment) => ({
      text: subsegmentText(subsegment),
      score: logprobToPercent(subsegment.logprob),
      start:
        typeof subsegment.start === 'number' && Number.isFinite(subsegment.start) ? subsegment.start : segment.start,
      end: typeof subsegment.end === 'number' && Number.isFinite(subsegment.end) ? subsegment.end : segment.end,
    }))
    .filter((part) => part.text !== '');
}

function normalizeSpeakerCorrections(corrections) {
  if (!isRecord(corrections)) return { aliases: {}, names: {} };
  return {
    aliases: isRecord(corrections.aliases) ? { ...corrections.aliases } : {},
    names: isRecord(corrections.names) ? { ...corrections.names } : {},
  };
}

function resolveSpeakerId(speakerId, corrections) {
  return corrections.aliases[speakerId] || speakerId;
}

function buildSpeakerMap(result, corrections = { aliases: {}, names: {} }) {
  const speaker_map = {};
  for (const segment of getResultSegments(result)) {
    const speakerId = resolveSpeakerId(segmentSpeaker(segment), corrections);
    if (!speakerId || speaker_map[speakerId]) {
      continue;
    }
    speaker_map[speakerId] = { name: corrections.names[speakerId] || speakerId, is_editing: false };
  }
  return speaker_map;
}

/**
 * A transcription as delivered by the transcription backend
 *
 */
export class SingleTranscript {
  constructor(
    job_id,
    result,
    speaker_map = null,
    matched_terms = {},
    translation_segments = [],
    language = null,
    speaker_corrections = {},
    speaker_corrections_revision = 0,
  ) {
    assertString(job_id, 'job_id');
    assertDictionary(result, 'result');
    assertArray(result.segments, 'result.segments');
    result.segments.forEach((segment, index) => {
      assertDictionary(segment, `result.segments[${index}]`);
      assertArray(segment.subsegments, `result.segments[${index}].subsegments`);
    });
    if (result.words != null) {
      assertArray(result.words, 'result.words');
    }
    const normalizedResult = normalizeResult(result);
    const normalizedCorrections = normalizeSpeakerCorrections(speaker_corrections);
    if (speaker_map !== null) {
      assertDictionary(speaker_map, 'speaker_map');
    }
    const resolvedSpeakerMap = buildSpeakerMap(normalizedResult, normalizedCorrections);
    assertDictionary(matched_terms, 'matched_terms');
    assertArray(translation_segments, 'translation_segments');
    translation_segments.forEach((segment, index) => assertString(segment, `translation_segments[${index}]`));
    if (language != null) {
      assertString(language, 'language');
    }

    this.job_id = job_id;
    this.result = normalizedResult;
    this.speaker_map = resolvedSpeakerMap;
    this.matched_terms = matched_terms;
    this.translation_segments = translation_segments;
    this.language = language;
    this.speaker_corrections = normalizedCorrections;
    this.speaker_corrections_revision =
      Number.isInteger(speaker_corrections_revision) && speaker_corrections_revision >= 0
        ? speaker_corrections_revision
        : 0;
  }

  getResult() {
    return this.result;
  }

  getSpeakerMap() {
    return this.speaker_map;
  }

  getSpeakerCorrections() {
    return normalizeSpeakerCorrections(this.speaker_corrections);
  }

  getSpeakerCorrectionsRevision() {
    return this.speaker_corrections_revision;
  }

  setSpeakerCorrectionsRevision(revision) {
    this.speaker_corrections_revision = Number.isInteger(revision) && revision >= 0 ? revision : 0;
  }

  applySpeakerCorrections(corrections) {
    this.speaker_corrections = normalizeSpeakerCorrections(corrections);
    this.speaker_map = buildSpeakerMap(this.result, this.speaker_corrections);
  }

  getMatchedTerms() {
    return this.matched_terms;
  }

  hasMatchedTerms() {
    return Object.keys(this.matched_terms).length !== 0;
  }

  getLanguage() {
    return this.language;
  }

  hasTranslation() {
    return this.translation_segments.length > 0;
  }

  getSpeakerLabel(speakerId) {
    const speaker = this.speaker_map[speakerId];
    if (speaker && speaker.name) {
      return speaker.name;
    }
    return speakerId;
  }

  getTranscriptRows() {
    const segments = getResultSegments(this.result);
    return segments.map((segment) => {
      return {
        speakerId: resolveSpeakerId(segmentSpeaker(segment), this.speaker_corrections),
        timeStamp: formatTimestamp(segment.start, segment.end),
        parts: buildPartsFromSegment(segment),
      };
    });
  }

  getTranslationRows() {
    if (this.translation_segments.length === 0) {
      return [];
    }

    const segments = getResultSegments(this.result);
    return segments.map((segment, index) => ({
      speakerId: resolveSpeakerId(segmentSpeaker(segment), this.speaker_corrections),
      timeStamp: formatTimestamp(segment.start, segment.end),
      text: this.translation_segments[index] ?? '',
    }));
  }

  getTranslationSegments() {
    return this.translation_segments;
  }

  getTranslationString() {
    return this.getTranslationRows()
      .map((row) => {
        if (!row.text) {
          return '';
        }
        const meta = [];
        const speakerLabel = this.getSpeakerLabel(row.speakerId);
        if (speakerLabel) {
          meta.push(speakerLabel);
        }
        if (row.timeStamp) {
          meta.push(`[${row.timeStamp}]`);
        }
        if (meta.length === 0) {
          return row.text;
        }
        return `${meta.join(' ')}\n${row.text}`;
      })
      .filter(Boolean)
      .join('\n\n');
  }

  toJSON() {
    return {
      job_id: this.job_id,
      result: this.result,
      speaker_map: this.speaker_map,
      matched_terms: this.matched_terms,
      translation_segments: this.translation_segments,
      language: this.language,
      speaker_corrections: this.speaker_corrections,
      speaker_corrections_revision: this.speaker_corrections_revision,
    };
  }

  static fromJSON(json) {
    if (!json) return null;
    if (!isRecord(json.result)) return null;

    const translation_segments = Array.isArray(json.translation_segments)
      ? json.translation_segments.map((segment) => (typeof segment === 'string' ? segment : ''))
      : [];

    return new SingleTranscript(
      json.job_id,
      json.result,
      json.speaker_map ?? null,
      json.matched_terms ?? {},
      translation_segments,
      json.language ?? null,
      json.speaker_corrections ?? {},
      json.speaker_corrections_revision ?? 0,
    );
  }
}
